import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import InfoHint from '../common/InfoHint';
import apiService from '../../services/api';

export interface PathfinderTrackFilters {
  hide_implausible_movement: boolean;
  implausible_max_speed_kmh: number;
  hide_gaps_default: boolean;
  gap_max_time_minutes: number;
  gap_max_distance_m: number;
  updated_at?: string | null;
  updated_by?: string | null;
}

const DEFAULTS: PathfinderTrackFilters = {
  hide_implausible_movement: true,
  implausible_max_speed_kmh: 160,
  hide_gaps_default: false,
  gap_max_time_minutes: 30,
  gap_max_distance_m: 500,
};

const kmhToMph = (kmh: number) => Math.round(kmh * 0.621371);

/**
 * Admin panel: org-wide Pathfinder track-filter defaults (system_settings).
 * Users who customized Map Layer Controls keep their session until they reset.
 */
const PathfinderTrackFiltersAdmin: React.FC = () => {
  const [settings, setSettings] = useState<PathfinderTrackFilters>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiService.client.get<PathfinderTrackFilters>(
        '/admin/system-settings/pathfinder-track-filters',
        { timeout: 30000 },
      );
      setSettings({ ...DEFAULTS, ...resp.data });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load Pathfinder track filters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await apiService.client.put<PathfinderTrackFilters>(
        '/admin/system-settings/pathfinder-track-filters',
        {
          hide_implausible_movement: settings.hide_implausible_movement,
          implausible_max_speed_kmh: settings.implausible_max_speed_kmh,
          hide_gaps_default: settings.hide_gaps_default,
          gap_max_time_minutes: settings.gap_max_time_minutes,
          gap_max_distance_m: settings.gap_max_distance_m,
        },
        { timeout: 30000 },
      );
      setSettings({ ...DEFAULTS, ...resp.data });
      setSuccess('Pathfinder track filters saved. New sessions and “Use org defaults” pick these up without redeploy.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save Pathfinder track filters');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Pathfinder Track Filters
        </Typography>
        <InfoHint title="Org defaults (no redeploy)">
          <p>
            These values seed Pathfinder map filters for all users. Analysts can still override them
            in Map Layer Controls for their browser session. Users who already customized map
            filters keep their session until they click “Use org defaults”.
          </p>
          <ul>
            <li>
              <strong>160 km/h (~100 mph)</strong> — recommended for trainee phone/vehicle data
            </li>
            <li>
              <strong>250 km/h (~155 mph)</strong> — old hard-coded behavior (extreme teleports only)
            </li>
            <li>Lower (e.g. 120) is stricter and may split aggressive highway driving</li>
          </ul>
        </InfoHint>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Implausible movement
          </Typography>
          <FormControlLabel
            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
            labelPlacement="start"
            control={
              <Switch
                color="primary"
                checked={settings.hide_implausible_movement}
                disabled={loading || saving}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, hide_implausible_movement: e.target.checked }))
                }
              />
            }
            label="Hide implausible movement (default)"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
            Max hop speed:{' '}
            <strong>
              {settings.implausible_max_speed_kmh} km/h (~{kmhToMph(settings.implausible_max_speed_kmh)} mph)
            </strong>
          </Typography>
          <Slider
            value={settings.implausible_max_speed_kmh}
            min={80}
            max={300}
            step={10}
            marks={[
              { value: 120, label: '120' },
              { value: 160, label: '160' },
              { value: 250, label: '250' },
            ]}
            disabled={loading || saving || !settings.hide_implausible_movement}
            onChange={(_, v) =>
              setSettings((s) => ({ ...s, implausible_max_speed_kmh: v as number }))
            }
            valueLabelDisplay="auto"
            sx={{ maxWidth: 480 }}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            GPS gaps
          </Typography>
          <FormControlLabel
            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
            labelPlacement="start"
            control={
              <Switch
                color="primary"
                checked={settings.hide_gaps_default}
                disabled={loading || saving}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, hide_gaps_default: e.target.checked }))
                }
              />
            }
            label="Hide GPS gaps (default)"
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Gap max time (minutes)"
              type="number"
              size="small"
              value={settings.gap_max_time_minutes}
              disabled={loading || saving}
              inputProps={{ min: 5, max: 180 }}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  gap_max_time_minutes: Math.max(5, Math.min(180, Number(e.target.value) || 5)),
                }))
              }
              sx={{ maxWidth: 220 }}
            />
            <TextField
              label="Gap max distance (m)"
              type="number"
              size="small"
              value={settings.gap_max_distance_m}
              disabled={loading || saving}
              inputProps={{ min: 100, max: 5000 }}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  gap_max_distance_m: Math.max(100, Math.min(5000, Number(e.target.value) || 100)),
                }))
              }
              sx={{ maxWidth: 220 }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={save}
          disabled={loading || saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {(settings.updated_at || settings.updated_by) && (
          <Typography variant="caption" color="text.secondary">
            Last updated
            {settings.updated_by ? ` by ${settings.updated_by}` : ''}
            {settings.updated_at ? ` · ${new Date(settings.updated_at).toLocaleString()}` : ''}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default PathfinderTrackFiltersAdmin;
